import type { ComponentType } from 'react';

import type { DashboardWidgetWidth } from '../model';
import { dashboardWidgetWidthClassName } from './dashboardWidgetWidthClassName';

export interface dashboardCanvasWidget {
  id: string;
  type: string;
  width: DashboardWidgetWidth;
}

export interface dashboardCanvasDocument<TWidget extends dashboardCanvasWidget> {
  version: number;
  widgets: readonly TWidget[];
}

export interface dashboardCanvasRendererProps<TWidget extends dashboardCanvasWidget, TRuntime> {
  widget: TWidget;
  runtime: TRuntime;
}

export interface dashboardWidgetDefinition<TWidget extends dashboardCanvasWidget, TRuntime> {
  twoPerMobileRow?: boolean;
  schema: {
    safeParse(value: unknown): { success: boolean };
  };
  component: ComponentType<dashboardCanvasRendererProps<TWidget, TRuntime>>;
}

export type dashboardWidgetRegistry<TWidget extends dashboardCanvasWidget, TRuntime> = Partial<
  Record<string, dashboardWidgetDefinition<TWidget, TRuntime>>
>;

interface dashboardCanvasProps<TWidget extends dashboardCanvasWidget, TRuntime> {
  document: dashboardCanvasDocument<TWidget>;
  registry: dashboardWidgetRegistry<TWidget, TRuntime>;
  runtime: TRuntime;
}

export function DashboardCanvas<TWidget extends dashboardCanvasWidget, TRuntime>({
  document,
  registry,
  runtime,
}: dashboardCanvasProps<TWidget, TRuntime>) {
  return (
    <section className="@container/dashboard grid min-w-0 grid-cols-2 items-stretch gap-5 md:grid-cols-12">
      {document.widgets.map((widget) => {
        const definition = registry[widget.type];
        if (!definition || !definition.schema.safeParse(widget).success) return null;
        const Component = definition.component;
        return (
          <div
            key={widget.id}
            id={`dashboard-widget-${widget.id}`}
            tabIndex={-1}
            style={{ scrollMarginTop: '7rem' }}
            className={dashboardWidgetWidthClassName(widget.width, definition.twoPerMobileRow)}
            data-widget-id={widget.id}
          >
            <Component widget={widget} runtime={runtime} />
          </div>
        );
      })}
    </section>
  );
}
